/**
 * VP Hero Slider — lightweight vanilla JS controller
 * Handles: autoplay, dot/arrow navigation, swipe (touch), pause on
 * hover/focus, and lazy-loading of slide videos. No dependencies.
 */
(function () {
  if (customElements.get('vp-hero-slider')) return;

  class VpHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.slides = [];
      this.dots = [];
      this.current = 0;
      this.timer = null;
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.track = this.querySelector('.vp-hero__track');
      this.slides = Array.from(this.querySelectorAll('.vp-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.vp-hero__dot'));
      this.prevBtn = this.querySelector('.vp-hero__arrow--prev');
      this.nextBtn = this.querySelector('.vp-hero__arrow--next');

      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.speed = parseInt(this.getAttribute('data-speed'), 10) || 6000;

      if (this.slides.length <= 1) return;

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goTo(i, true));
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.goTo(this.current - 1, true));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.goTo(this.current + 1, true));
      }

      // Swipe support
      this.track.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.track.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      }, { passive: true });

      // Pause autoplay on hover/focus for accessibility
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    handleSwipe() {
      const delta = this.touchStartX - this.touchEndX;
      const threshold = 50;
      if (delta > threshold) {
        this.goTo(this.current + 1, true);
      } else if (delta < -threshold) {
        this.goTo(this.current - 1, true);
      }
    }

    startAutoplay() {
      if (!this.autoplay || this.slides.length <= 1) return;
      this.stopAutoplay();
      this.timer = setInterval(() => {
        this.goTo(this.current + 1);
      }, this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    goTo(index, userInitiated) {
      const total = this.slides.length;
      const next = ((index % total) + total) % total;
      if (next === this.current) return;

      const currentSlide = this.slides[this.current];
      const nextSlide = this.slides[next];

      currentSlide.classList.remove('is-active');
      nextSlide.classList.add('is-active');

      // Pause video on outgoing slide
      const outgoingVideo = currentSlide.querySelector('video');
      if (outgoingVideo) outgoingVideo.pause();

      // Lazy-load + play video on incoming slide
      const incomingVideo = nextSlide.querySelector('video');
      if (incomingVideo) {
        if (!incomingVideo.src && incomingVideo.dataset.src) {
          incomingVideo.src = incomingVideo.dataset.src;
        }
        incomingVideo.play().catch(() => {});
      }

      this.dots.forEach((dot, i) => {
        const isActive = i === next;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.current = next;

      if (userInitiated) {
        this.startAutoplay();
      }
    }
  }

  customElements.define('vp-hero-slider', VpHeroSlider);
})();
